import { Accordion, AccordionSummary, Box, Grid, styled, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { routes } from 'Routes';
import { CompanySSOConnectionDetails } from './edit-company-sso.types';
import { SsoConnectionCopyFields } from './sso-connection-copy-fields';
import { useGetSSOSettings } from './use-get-sso-settings';
import { CompanyDetailAndJobGroups } from '../../../../../../../types';
import { IconApproved, IconCancel, IconSetting2 } from '../../../../../../../assets/img';
import { LwButton, theme } from '../../../../../../../redesign';
import { useCompanyRelativePath } from '../../../../../../hooks';
import { RenderUnsettledUI } from '../../../../../../../shared/components';

export const CompanyDetailSSO = ({ data }: { data: CompanyDetailAndJobGroups }) => {
  const generatePath = useCompanyRelativePath();
  const { t } = useTranslation('ogp-screen-specific');
  const { isWorkerSSOEnabled, isOgpUserSSOEnabled } = data.company;
  const ssoSettingsQuery = useGetSSOSettings();

  if (ssoSettingsQuery.status !== 'success') {
    return <RenderUnsettledUI data={ssoSettingsQuery} />;
  }

  return (
    <Box role="tabpanel">
      <Grid container xs={2} pl={0} mb={3}>
        <LwButton
          iconColorMode="stroke"
          startIcon={<IconSetting2 />}
          color="secondary"
          to={generatePath(routes.SSOSettingsEdit)}
        >
          {t('sso-settings.edit-button.label')}
        </LwButton>
      </Grid>
      <StyledAccordion expanded={isOgpUserSSOEnabled}>
        <StyledAccordionSummary>
          <Section>
            <AccordionHeader
              isAccordionActive={isOgpUserSSOEnabled}
              title={t('sso-settings.ogp-user.title')}
            />
          </Section>
        </StyledAccordionSummary>
        <AccordionContent
          connectionDetails={ssoSettingsQuery.data.ogpSsoSettings.connectionDetails}
          emailClaim={ssoSettingsQuery.data.ogpSsoSettings.emailClaim}
        />
      </StyledAccordion>
      <StyledAccordion expanded={isWorkerSSOEnabled}>
        <StyledAccordionSummary>
          <Section>
            <AccordionHeader
              isAccordionActive={isWorkerSSOEnabled}
              title={t('sso-settings.worker.title')}
            />
          </Section>
        </StyledAccordionSummary>
        <AccordionContent
          connectionDetails={ssoSettingsQuery.data.workerSsoSettings.connectionDetails}
          emailClaim={ssoSettingsQuery.data.workerSsoSettings.emailClaim}
        />
      </StyledAccordion>
    </Box>
  );
};
const AccordionHeader = ({
  isAccordionActive,
  title,
}: {
  isAccordionActive: boolean;
  title: string;
}) => {
  const { t } = useTranslation('ogp-screen-specific');
  return (
    <StyledHeaderContainer>
      <Typography variant="h3">{title}</Typography>
      {isAccordionActive ? (
        <StyledStatusContainer>
          <Typography variant="body1" color="secondary">
            {t('sso-settings.active-status.label')}
          </Typography>
          <StatusIcon>
            <IconApproved />
          </StatusIcon>
        </StyledStatusContainer>
      ) : (
        <StyledStatusContainer>
          <Typography variant="body1" color="secondary">
            {t('sso-settings.inactive-status.label')}
          </Typography>
          <StatusIcon>
            <IconCancel />
          </StatusIcon>
        </StyledStatusContainer>
      )}
    </StyledHeaderContainer>
  );
};
const AccordionContent = ({
  connectionDetails,
  emailClaim,
}: {
  connectionDetails: CompanySSOConnectionDetails;
  emailClaim: string;
}) => {
  const { t } = useTranslation('ogp-screen-specific');
  return (
    <Section>
      <SsoConnectionCopyFields
        connectionDetails={connectionDetails}
        entityIdLabel={t('sso-settings.sso-entity-id.label')}
        acsUrlLabel={t('sso-settings.sso-acs-url.label')}
        relayStateLabel={t('sso-settings.sso-relay-state.label')}
      />
      <Grid container spacing={2} mt={2}>
        <Grid item xs={6}>
          <Typography variant="subtitle2" color="secondary" noWrap>
            {t('sso-settings.platform-mapping.label')}
          </Typography>
          <Typography variant="body1" noWrap>
            email
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle2" color="secondary" noWrap>
            {t('sso-settings.identity-provider-attribute.label')}
          </Typography>
          <Typography variant="body1" noWrap>
            {emailClaim}
          </Typography>
        </Grid>
      </Grid>
    </Section>
  );
};
const StyledAccordion = styled(Accordion)<{ expanded: boolean }>(() => ({
  justifyContent: 'flex-start',
  boxShadow: 'none',
  '& .MuiAccordionSummary-expandIconWrapper': {},
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '&.MuiAccordion-root:before': {
    backgroundColor: theme.palette.background.paper,
  },
}));
const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  gap: theme.spacing(2),
  borderRadius: theme.spacing(4),
  padding: 0,
}));

const Section = styled(Box)({
  width: '50%',
});

const StyledHeaderContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.lwPrimaryAccentDark[5],
  borderRadius: theme.spacing(4),
  padding: theme.spacing(4),
  display: 'flex',
  alignItems: 'center',
  alignContent: 'space-between',
  justifyContent: 'space-between',
  width: '100%',
  border: '1px solid transparent',
}));

const StyledStatusContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const StatusIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: theme.spacing(1),
}));
